<template>
    <TriadPullToRefresh :refreshing="isRefreshing" @refresh="handleRefresh">
        <FormView :title="$t('profileSection.title')">
            <template #headerActions>
                <div class="flex items-center gap-2">
                    <!-- Tour Guide Trigger Button -->
                    <button
                        type="button"
                        @click="() => startProfileTour(true)"
                        :title="$t('tour.startTour')"
                        class="p-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 shadow-sm active:scale-95 transition-all cursor-pointer flex items-center justify-center">
                        <QuestionMarkCircleIcon class="w-4 h-4" />
                    </button>

                    <!-- Reload Button for quick manual refresh on Desktop & Mobile -->
                    <button
                        type="button"
                        @click="handleRefresh"
                        :disabled="isRefreshing"
                        :title="$t('profileSection.reload') || 'Recarregar perfil'"
                        class="p-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 shadow-sm active:scale-95 transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center">
                        <ArrowPathIcon class="w-4 h-4 text-indigo-600 dark:text-indigo-400" :class="{ 'animate-spin': isRefreshing }" />
                    </button>

                    <button v-if="isSubscriptionEnabled() && isFree"
                            type="button"
                            @click="openModal"
                            class="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-xs sm:text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center gap-1.5 cursor-pointer active:scale-95">
                        <SparklesIcon class="w-4 h-4" />
                        <span>{{ $t('subscriptionPage.subscribeBtn') || 'Assinar' }}</span>
                    </button>
                </div>
            </template>
            <template #mainForm>
                <div :key="refreshKey" class="flex flex-col gap-6 max-w-4xl mx-auto bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
                    <div data-tour="profile-avatar">
                        <AvatarConfig :profile="profile" @avatar-updated="url => profile.avatarUrl = url" />
                    </div>
                    
                    <hr class="border-gray-100 dark:border-gray-700" />
                    
                    <div data-tour="profile-basic-info">
                        <div class="text-lg font-bold text-gray-900 dark:text-white mb-2">{{ $t('profileSection.basicInformation') }}</div>
                        <BasicInfo />
                    </div>
                    
                    <hr class="border-gray-100 dark:border-gray-700" />
                    
                    <div data-tour="profile-preferences">
                        <div class="text-lg font-bold text-gray-900 dark:text-white mb-2">{{ $t('profileSection.preferences', 'Preferências do Sistema') }}</div>
                        <PreferencesInfo />
                    </div>
                    
                    <hr class="border-gray-100 dark:border-gray-700" />
                    
                    <div data-tour="profile-notifications">
                        <div class="text-lg font-bold text-gray-900 dark:text-white mb-2">{{ $t('profileSection.notifications') }}</div>
                        <NotificationsInfo />
                    </div>
                    
                    <hr class="border-gray-100 dark:border-gray-700" />
                    
                    <div data-tour="profile-membership">
                        <div class="text-lg font-bold text-gray-900 dark:text-white mb-2">{{ $t('subscriptionPage.title', 'Assinatura & Planos') }}</div>
                        <MembershipInfo />
                    </div>
                    
                    <hr class="border-gray-100 dark:border-gray-700" />
                    
                    <div data-tour="profile-security">
                        <div class="text-lg font-bold text-gray-900 dark:text-white mb-2">{{ $t('profileSection.security') }}</div>
                        <SecurityInfo :email="profile.email" />
                    </div>
                    
                    <hr class="border-gray-100 dark:border-gray-700" />
                    
                    <div>
                        <div class="text-lg font-bold text-gray-900 dark:text-white mb-2">{{ $t('profileSection.legal', 'Legal & Compliance') }}</div>
                        <div class="flex flex-wrap gap-3 text-sm">
                            <a href="https://triadmusic.app/terms" target="_blank" rel="noopener noreferrer" @click.prevent="openExternalUrl('https://triadmusic.app/terms')" class="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 text-slate-700 dark:text-slate-200 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium transition-colors border border-gray-200 dark:border-gray-700">
                                {{ $t('legal.termsOfService', 'Terms of Service') }}
                            </a>
                            <a href="https://triadmusic.app/privacy" target="_blank" rel="noopener noreferrer" @click.prevent="openExternalUrl('https://triadmusic.app/privacy')" class="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 text-slate-700 dark:text-slate-200 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium transition-colors border border-gray-200 dark:border-gray-700">
                                {{ $t('legal.privacyPolicy', 'Privacy Policy') }}
                            </a>
                            <a href="https://triadmusic.app/copyright" target="_blank" rel="noopener noreferrer" @click.prevent="openExternalUrl('https://triadmusic.app/copyright')" class="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 text-slate-700 dark:text-slate-200 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium transition-colors border border-gray-200 dark:border-gray-700">
                                {{ $t('legal.dmcaPolicy', 'DMCA & Copyright') }}
                            </a>
                        </div>
                    </div>
                    
                    <hr class="border-gray-100 dark:border-gray-700" />
                    
                    <div data-tour="profile-danger">
                        <div class="text-lg font-bold text-red-600 dark:text-red-400 mb-2">{{ $t('profileSection.dangerZone') }}</div>
                        <DangerZone :email="profile.email" />
                    </div>
                </div>
            </template>
        </FormView>
    </TriadPullToRefresh>
</template>

<script setup>
import AvatarConfig from '@/components/user/profile/AvatarConfig.vue'
import BasicInfo from '@/components/user/profile/BasicInfo.vue'
import PreferencesInfo from '@/components/user/profile/PreferencesInfo.vue'
import SecurityInfo from '@/components/user/profile/SecurityInfo.vue'
import DangerZone from '@/components/user/profile/DangerZone.vue'
import NotificationsInfo from '@/components/user/profile/NotificationsInfo.vue'
import MembershipInfo from '@/components/user/profile/MembershipInfo.vue'
import TriadPullToRefresh from '@/components/common/elements/TriadPullToRefresh.vue'
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { SparklesIcon } from '@heroicons/vue/24/solid'
import { ArrowPathIcon, QuestionMarkCircleIcon } from '@heroicons/vue/24/outline'
import { openSubscriptionModal, appEvents } from '@/store/app.events'
import toastStore from '@/store/toast.store'
import userStore from '@/store/user.store'
import { isSubscriptionEnabled } from '@/service/subscription.service'
import { openExternalUrl } from '@/utils/capacitor'
import { useTour } from '@/composables/useTour'

const { t } = useI18n()
const { startProfileTour } = useTour()

const isFree = computed(() => userStore.getters['isFree'])

const openModal = () => {
  openSubscriptionModal()
}

const profile = ref({
  name: '',
  email: '',
  avatarUrl: null
})
const loading = ref(true)
const isRefreshing = ref(false)
const refreshKey = ref(0)

const fetchProfileData = async (silent = false) => {
  try {
    // 1. Força atualização do userStore (ignora cache da promessa e atualiza o estado + localStorage)
    await userStore.dispatch('info', true)

    // 2. Busca informações atualizadas do endpoint
    const response = await fetch('/api/user/info')
    if (!response.ok) {
      if (response.status !== 401) {
        throw new Error(`HTTP ${response.status}`)
      }
      return
    }
    const data = await response.json()
    if (!data || (!data.id && !data.email && !data.name)) {
      throw new Error('Invalid profile response')
    }
    profile.value = {
      name: data.name,
      email: data.email,
      avatarUrl: data.avatarUrl || data.avatar_url || null
    }
  } catch (error) {
    if (!silent) {
      toastStore.dispatch('addMessage', {
        message: t('profileSection.profileLoadFailed') || 'Failed to load profile information',
        icon: 'error'
      })
    }
    throw error
  }
}

const handleRefresh = async () => {
  if (isRefreshing.value) return
  isRefreshing.value = true
  const startTime = Date.now()
  try {
    await fetchProfileData(true)
    // Remonta os componentes filhos para recarregarem com dados atualizados
    refreshKey.value++

    // Garante que o feedback visual da animação fique visível por pelo menos 450ms
    const elapsed = Date.now() - startTime
    if (elapsed < 450) {
      await new Promise(resolve => setTimeout(resolve, 450 - elapsed))
    }

    toastStore.dispatch('addMessage', {
      message: t('profileSection.profileRefreshed') || 'Perfil atualizado com sucesso',
      icon: 'success'
    })
  } catch (err) {
    toastStore.dispatch('addMessage', {
      message: t('profileSection.profileLoadFailed') || 'Erro ao carregar informações do perfil',
      icon: 'error'
    })
  } finally {
    isRefreshing.value = false
  }
}

onMounted(async () => {
  appEvents.on('reloadProfile', handleRefresh)
  try {
    await fetchProfileData(false)
  } catch (e) {
    console.warn('Initial profile load failed:', e)
  } finally {
    loading.value = false
  }
  startProfileTour(false, 900)
})

onBeforeUnmount(() => {
  appEvents.off('reloadProfile', handleRefresh)
})
</script>